import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Upload } from 'lucide-react';
import { useContacts } from '../hooks/useContacts';
import { useCompanyOptions } from '../hooks/useCompanyOptions';
import ContactGridView from '../components/contacts/ContactGrid';
import ContactListView from '../components/contacts/ContactList';
import ImportContacts from '../components/contacts/ImportContacts';
import AddContactPopup from '../components/contacts/AddContactPopup';
import SearchBar from '../components/contacts/SearchBar';
import ViewToggle from '../components/contacts/ViewToggle';
import NoContactFilter from '../components/contacts/NoContactFilter';
import RefreshAllButton from '../components/contacts/RefreshAllButton';
import LoadingPage from '../components/ui/LoadingPage';

export default function ContactList() {
  const { contacts, loading, error, refetch } = useContacts();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showImport, setShowImport] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [view, setView] = useState<'grid' | 'list'>('list');
  const [noContactDays, setNoContactDays] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string>(searchParams.get('company') || '');
  const [isMobile] = useState(() => window.innerWidth < 768);
  const { options: companyOptions } = useCompanyOptions();
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCompany) params.set('company', selectedCompany);
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedCompany, setSearchParams]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check for Cmd+A (Mac) or Ctrl+A (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault(); // Prevent default select-all behavior
        setShowAddContact(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Apply all filters in a single useMemo
  const filteredContacts = useMemo(() => {
    // First apply search filter
    let filtered = contacts.filter(contact => {
      if (!searchQuery) return true;
      
      const query = searchQuery.toLowerCase();
      const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
      const email = [contact.personal_email, contact.work_email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      
      return fullName.includes(query) || email.includes(query);
    });

    // Then apply company filter
    filtered = filtered.filter(contact => 
      !selectedCompany || 
      (selectedCompany === 'none' && !contact.company_id) ||
      contact.company_id === selectedCompany
    );

    // Finally apply no contact filter
    if (noContactDays) {
      if (noContactDays === -1) {
        // "Ever" filter - show contacts with no contact history
        filtered = filtered.filter(contact => !contact.last_contact_date);
      } else {
        // Calculate cutoff date
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - noContactDays);

        // Filter contacts with no recent contact
        filtered = filtered.filter(contact => {
          if (!contact.last_contact_date) return true;
          return new Date(contact.last_contact_date) < cutoffDate;
        });
      }
    }

    return filtered;
  }, [contacts, searchQuery, selectedCompany, noContactDays]);

  if (loading) {
    return <LoadingPage message="Loading your contacts..." />;
  }

  if (error) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h1 className="text-2xl font-bold hidden md:block">Contacts</h1>
        <div className="flex items-center gap-4">
          <div className="md:hidden">
            <RefreshAllButton onSuccess={refetch} iconOnly />
          </div>
          <div className="hidden md:block">
            <RefreshAllButton onSuccess={refetch} />
          </div>
          
          <div className="md:hidden">
            <button 
              onClick={() => setShowImport(true)}
              className="btn btn-secondary h-[42px] w-[42px] flex items-center justify-center p-0"
              title="Import Contacts"
            >
              <Upload className="w-5 h-5" />
            </button>
          </div>
          <div className="hidden md:block">
            <button 
              onClick={() => setShowImport(true)}
              className="btn btn-secondary"
            >
              Import Contacts
            </button>
          </div>
          
          <div className="md:hidden">
            <button 
              onClick={() => setShowAddContact(true)}
              className="btn btn-primary h-[42px] w-[42px] flex items-center justify-center p-0"
              title="Add Contact"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="hidden md:block">
            <button 
              onClick={() => setShowAddContact(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Contact
              <span className="text-xs text-gray-400">⌘A</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-sm text-gray-400">Search</label>
              <SearchBar 
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-sm text-gray-400">Filter By Company</label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="input bg-dark-800 h-[42px]"
              >
                <option value="">All Companies</option>
                <option value="none">No Company Assigned</option>
                {companyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-end gap-4">
            <NoContactFilter
              selectedDays={noContactDays}
              onSelect={setNoContactDays}
              className="flex-1"
            />
            <div className="hidden md:block ml-auto">
              <ViewToggle view={view} onViewChange={setView} />
            </div>
          </div>
        </div>
      </div>

      {filteredContacts.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          {contacts.length === 0 
            ? "No contacts found. Add your first contact to get started."
            : "No contacts match your search."}
        </div>
      ) : isMobile ? (
        <ContactGridView contacts={filteredContacts} />
      ) : view === 'grid' ? (
        <ContactGridView contacts={filteredContacts} />
      ) : (
        <ContactListView 
          contacts={filteredContacts}
          onRefresh={refetch}
        />
      )}

      {showImport && (
        <ImportContacts
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            refetch();
            setShowImport(false);
          }}
        />
      )}

      {showAddContact && (
        <AddContactPopup
          onClose={() => setShowAddContact(false)}
          onSuccess={() => {
            refetch();
            setShowAddContact(false);
          }}
        />
      )}
    </div>
  );
}